import {
  RETURN_CONDITION_AREAS,
  RETURN_CONDITION_LABELS,
  RETURN_CONDITIONS,
  RETURN_DAMAGE_REASONS,
  RETURN_UNDO_MINUTES,
  barcodeCandidates,
  scanSpellings,
  type CustomerReturnView,
  type ReceiveReturnResult,
  type ReturnCondition,
  type ReturnLookupMatch,
  type ReturnableItem,
  type UndoReturnResult,
} from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage, type FloorAction } from '../api';
import { feedback } from '../feedback';
import { customerText, fmt } from '../format';
import { useSession } from '../session';
import { Empty, ErrorBanner, Modal, ProductThumb, ScanField, SellerChip, Spinner, StatusChip, useFloorAction, useLoad } from '../ui';
import { Icon } from '../ui/icons';
import { NextStep, NextWorkStep } from '../ui/NextStep';

const CONDITION_TONE: Readonly<Record<ReturnCondition, string>> = { sellable: 'ok', damaged: 'bad', defective: 'bad', opened: 'warn' };
const AREA_LABEL = { returns: 'iade alanına, oradan rafa', damaged: 'hasarlı ürün alanına', quarantine: 'karantinaya' } as const;

type Outcome = { readonly kind: 'received'; readonly result: ReceiveReturnResult } | { readonly kind: 'undone'; readonly result: UndoReturnResult; readonly units: number; readonly sellable: number };

/**
 * Geri al from the desk: the worker who received the return, within RETURN_UNDO_MINUTES, or a
 * supervisor later; never once a unit was put away (the API decides, this only offers it).
 */
function undoable(r: CustomerReturnView, supervisor: boolean): boolean {
  if (r.status !== 'received' || r.storedUnits > 0) return false;
  if (supervisor) return true;
  return r.receivedByMe && r.receivedAt !== null && Date.now() - new Date(r.receivedAt).getTime() < RETURN_UNDO_MINUTES * 60_000;
}

/**
 * İade kabul masası: the code on the returned parcel (the return cargo code the seller
 * announced, the order number or the original tracking number) brings up the order; every unit
 * is sorted by condition and the return is closed in one step. A return received by mistake is
 * taken back ("Geri al") and received again.
 */
export function ReturnsDesk({ onChange }: { onChange: () => void }) {
  const { warehouseId, isSupervisor } = useSession();
  const [matches, setMatches] = useState<readonly ReturnLookupMatch[] | null>(null);
  const [selected, setSelected] = useState<{ match: ReturnLookupMatch; scanned: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [undoing, setUndoing] = useState<CustomerReturnView | null>(null);
  const [tab, setTab] = useState<'expected' | 'received'>('expected');
  const list = useLoad(
    () => (warehouseId ? api<CustomerReturnView[]>('GET', `/api/returns?warehouseId=${warehouseId}&status=${tab}&limit=50`) : Promise.resolve([])),
    [warehouseId, tab],
    20_000,
  );

  const lookup = async (code: string) => {
    setError(null);
    setOutcome(null);
    try {
      const found = await api<ReturnLookupMatch[]>('GET', `/api/returns/lookup?code=${encodeURIComponent(code)}`);
      if (found.length === 0) {
        feedback('error');
        setError(`${code}: bu koda ait sipariş bulunamadı. Sipariş numarasını veya ilk gönderinin takip numarasını deneyin.`);
        setMatches(null);
        return;
      }
      feedback('ok');
      if (found.length === 1) {
        setSelected({ match: found[0]!, scanned: code });
        setMatches(null);
      } else {
        setMatches(found);
        setSelected(null);
      }
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
    }
  };

  return (
    <div className="split rdesk">
      <div className="stack">
        {selected ? (
          <ReturnForm
            key={selected.match.orderId}
            match={selected.match}
            scanned={selected.scanned}
            warehouseId={warehouseId!}
            onCancel={() => setSelected(null)}
            onUnsure={() => {
              setSelected(null);
              setError('İade kaydedildi mi belli değil. Koliyi yeniden okutup iade alınabilecek adede bakın.');
            }}
            onDone={(result) => {
              feedback('done');
              setSelected(null);
              setOutcome({ kind: 'received', result });
              void list.reload();
              onChange();
            }}
          />
        ) : (
          <section className="card">
            <div className="card-b stack">
              <ScanField
                placeholder="İade kargo kodu, sipariş no veya takip no okutun"
                allowTyping
                onScan={lookup}
                suggest={{ kinds: ['order'], orderStatuses: ['shipped', 'delivered'] }}
                hint="Pazaryerinin iade kodu, satıcının bildirdiği iade numarası (IADE…), sipariş numarası veya ilk gönderinin takip numarası."
              />
              {matches && (
                <div className="stack" style={{ gap: 8 }}>
                  <b>Bu koda uyan birden fazla sipariş var:</b>
                  <ul className="hit-list">
                    {matches.map((m) => (
                      <li key={m.orderId}>
                        <button className="hit-row" onClick={() => setSelected({ match: m, scanned: '' })}>
                          <SellerChip code={m.tenantCode} />
                          <b className="mono">{m.orderNumber}</b>
                          <span>{customerText(m)}</span>
                          <StatusChip status={m.status} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}
        {/* under the scan field: the next parcel is scanned without scrolling past the last result */}
        {error && <div className="banner bad">{error}</div>}
        {outcome?.kind === 'received' && (
          <ReceivedBanner
            result={outcome.result}
            canUndo={undoable(outcome.result.return, isSupervisor)}
            onUndo={() => setUndoing(outcome.result.return)}
            onClose={() => setOutcome(null)}
          />
        )}
        {outcome?.kind === 'received' && <ReturnNext sellable={outcome.result.return.sellableUnits} />}
        {outcome?.kind === 'undone' && <UndoneBanner result={outcome.result} units={outcome.units} sellable={outcome.sellable} onClose={() => setOutcome(null)} />}
      </div>
      <section className="card">
        <div className="card-h">
          <div className="tabs" role="tablist">
            <button className={tab === 'expected' ? 'on' : ''} role="tab" aria-selected={tab === 'expected'} onClick={() => setTab('expected')}>
              Beklenen iadeler
            </button>
            <button className={tab === 'received' ? 'on' : ''} role="tab" aria-selected={tab === 'received'} onClick={() => setTab('received')}>
              Son alınanlar
            </button>
          </div>
        </div>
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : list.data.length === 0 ? (
          <Empty title={tab === 'expected' ? 'Bildirilmiş iade yok' : 'Henüz iade alınmadı'}>
            {tab === 'expected' ? 'Satıcıların bildirdiği iadeler, kargo kodlarıyla burada listelenir.' : 'Masada teslim alınan iadeler burada görünür.'}
          </Empty>
        ) : (
          // a list, not a table: on a phone each return is one big row with its button in reach
          <ul className="rdesk-list">
            {list.data.map((r) => {
              const body = (
                <>
                  <span className="rdesk-row-main">
                    <span className="row" style={{ gap: 6 }}>
                      <SellerChip code={r.tenantCode} />
                      <b className="mono">{r.orderNumber}</b>
                    </span>
                    <span className="muted rdesk-row-sub">
                      {r.rmaNumber}
                      {r.returnTrackingNumber && ` · kargo ${r.returnTrackingNumber}`}
                      {r.customerReason && ` · ${r.customerReason}`}
                    </span>
                  </span>
                  {tab === 'received' ? (
                    <span className="rdesk-row-state">
                      <span className="chip ok">{r.sellableUnits} sağlam</span>
                      {r.blockedUnits > 0 && <span className="chip bad">{r.blockedUnits} ayrıldı</span>}
                      {r.receivedAt && <span className="muted">{fmt.age(r.receivedAt)} önce</span>}
                    </span>
                  ) : (
                    <span className="muted rdesk-row-state">{fmt.age(r.createdAt)} önce</span>
                  )}
                </>
              );
              return (
                <li key={r.id}>
                  {tab === 'expected' ? (
                    <button className="rdesk-row" onClick={() => void lookup(r.rmaNumber)}>
                      {body}
                    </button>
                  ) : (
                    <div className="rdesk-row">
                      {body}
                      {undoable(r, isSupervisor) && (
                        <button className="btn" onClick={() => setUndoing(r)}>
                          <Icon name="undo" /> Geri al
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {undoing && (
        <UndoReturnDialog
          ret={undoing}
          onClose={() => setUndoing(null)}
          onUndone={(result) => {
            feedback('ok');
            setUndoing(null);
            setOutcome({ kind: 'undone', result, units: undoing.sellableUnits + undoing.blockedUnits, sellable: undoing.sellableUnits });
            void list.reload();
            onChange();
          }}
        />
      )}
    </div>
  );
}

interface Line {
  readonly key: string;
  readonly orderItemId: string;
  readonly condition: ReturnCondition;
  readonly qty: number;
  readonly reason: string;
}

/** One returned parcel: the order's lines, every unit sorted into a condition. */
function ReturnForm(props: {
  match: ReturnLookupMatch;
  scanned: string;
  warehouseId: string;
  onCancel: () => void;
  /** a lost "İadeyi tamamla" was given up on: the parcel is looked up again, the form must not send it with a new key */
  onUnsure: () => void;
  onDone: (result: ReceiveReturnResult) => void;
}) {
  const { match } = props;
  // the staff read every unit; a supervisor may add one without reading it
  const { isSupervisor } = useSession();
  const announcedCode = match.expected?.returnTrackingNumber ?? null;
  // a code that is neither the order number nor the original tracking number is the return parcel's own
  // (either read by a reader on another keyboard layout, as the look-up found it, counts as them)
  const spellings = scanSpellings(props.scanned);
  const scannedIsReturnCode =
    props.scanned !== '' &&
    !spellings.includes(match.orderNumber.toUpperCase()) &&
    !(match.trackingNumber !== null && spellings.includes(match.trackingNumber)) &&
    !props.scanned.startsWith('IADE');
  const [code, setCode] = useState(announcedCode ?? (scannedIsReturnCode ? props.scanned : ''));
  const [lines, setLines] = useState<Line[]>([]);
  const [note, setNote] = useState('');
  const [asking, setAsking] = useState<ReturnableItem | null>(null);
  const [error, setError] = useState<string | null>(match.problem);
  const [busy, setBusy] = useState(false);
  // "İadeyi tamamla" whose answer was lost is sent again with its key: the return is booked once (the
  // form stays open meanwhile: closed, the key would go with it)
  const finish = useFloorAction({ onUnsure: props.onUnsure });

  const taken = (itemId: string) => lines.filter((l) => l.orderItemId === itemId).reduce((s, l) => s + l.qty, 0);
  const add = (item: ReturnableItem, condition: ReturnCondition, qty = 1) => {
    if (taken(item.orderItemId) + qty > item.returnable) {
      feedback('error');
      setError(`${item.sku ?? item.name}: en fazla ${item.returnable} adet iade alınabilir.`);
      return;
    }
    setError(match.problem);
    feedback('ok');
    setLines((all) => {
      const key = `${item.orderItemId}|${condition}`;
      return all.some((l) => l.key === key)
        ? all.map((l) => (l.key === key ? { ...l, qty: l.qty + qty } : l))
        : [...all, { key, orderItemId: item.orderItemId, condition, qty, reason: '' }];
    });
  };
  const change = (key: string, patch: Partial<Line>) => setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)).filter((l) => l.qty > 0));

  // a product scan asks for the unit's condition
  const onProduct = (scanned: string) => {
    const find = (codes: readonly string[], skus: readonly string[]) =>
      match.items.find((i) => i.barcodes.some((b) => codes.includes(b.code)) || skus.includes(i.sku?.toUpperCase() ?? ''));
    // the code as read; failing that, as a reader on another keyboard layout meant it (AB.12 ↔ AB/12)
    const item = find(barcodeCandidates(scanned).codes, [scanned]) ?? find(barcodeCandidates(scanned, { layouts: true }).codes, scanSpellings(scanned));
    if (!item) {
      feedback('error');
      setError(`${scanned}: bu siparişte böyle bir ürün yok.`);
      return;
    }
    setAsking(item);
  };

  const missingReason = lines.some((l) => l.condition !== 'sellable' && l.reason.trim() === '');
  const units = lines.reduce((s, l) => s + l.qty, 0);

  const submit = async (action: FloorAction) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<ReceiveReturnResult>(
        'POST',
        '/api/returns/receive',
        {
          warehouseId: props.warehouseId,
          orderId: match.orderId,
          returnTrackingNumber: code.trim() || null,
          note: note.trim() || null,
          lines: lines.map((l) => ({ orderItemId: l.orderItemId, qty: l.qty, condition: l.condition, reason: l.condition === 'sellable' ? null : l.reason.trim() })),
        },
        { action },
      );
      props.onDone(result);
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-h">
        <SellerChip code={match.tenantCode} />
        <h2 className="mono">{match.orderNumber}</h2>
        <StatusChip status={match.status} />
        <span className="spacer" />
        <button className="btn ghost" onClick={props.onCancel} disabled={busy || finish.waiting}>
          <Icon name="x" /> Vazgeç
        </button>
      </div>
      <div className="card-b stack">
        <div className="muted">
          {match.tenantName} · {customerText(match, 'Alıcı')}
          {match.city && ` · ${match.city}`}
          {match.shippedAt && ` · ${fmt.date(match.shippedAt)} kargolandı`}
          {match.trackingNumber && ` · takip ${match.trackingNumber}`}
        </div>
        {match.expected && (
          <div className="banner info">
            <Icon name="inbox" />
            <div style={{ flex: 1, minWidth: 0 }}>
              Satıcı bu iadeyi bildirdi: <b>{match.expected.rmaNumber}</b>
              {match.expected.customerReason && ` · Müşterinin nedeni: ${match.expected.customerReason}`}
            </div>
          </div>
        )}
        {error && <div className="banner bad">{error}</div>}
        {!match.problem && (
          <>
            {/* stays in view while the order's items scroll */}
            <div className="rcv-sticky">
              <ScanField placeholder="Ürün barkodunu okutun, kondisyonunu seçin" onScan={onProduct} hint="Her ürünü inceleyip okutun; açılan pencereden durumunu seçin." />
            </div>
            <div>
              {match.items.map((item) => (
                <div key={item.orderItemId} className="pack-line" style={{ gridTemplateColumns: 'auto 1fr' }}>
                  <ProductThumb name={item.name} imageUrl={item.imageUrl} small />
                  <div className="stack" style={{ gap: 6 }}>
                    <div>
                      <b>{item.name}</b> <span className="mono muted">{item.sku ?? 'eşleşmedi'}</span>
                      <div className="muted" style={{ fontSize: 12.5 }}>
                        Gönderilen {item.shipped}
                        {item.returned > 0 && ` · önceden iade ${item.returned}`} · bu iadede {taken(item.orderItemId)} / {item.returnable}
                      </div>
                    </div>
                    {isSupervisor && item.returnable > 0 && item.productId && (
                      <div className="row" style={{ gap: 6 }}>
                        {RETURN_CONDITIONS.map((c) => (
                          <button key={c} className={`chip select ${CONDITION_TONE[c]}`} onClick={() => add(item, c)} disabled={taken(item.orderItemId) >= item.returnable}>
                            + {RETURN_CONDITION_LABELS[c]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {lines.length > 0 && (
              <div className="stack" style={{ gap: 10 }}>
                <h3 style={{ margin: 0 }}>Ayrıştırma</h3>
                {lines.map((l) => {
                  const item = match.items.find((i) => i.orderItemId === l.orderItemId)!;
                  return (
                    <div key={l.key} className="price-card">
                      <div className="row" style={{ justifyContent: 'space-between' }}>
                        <div className="row" style={{ gap: 8 }}>
                          <span className={`chip ${CONDITION_TONE[l.condition]}`}>{RETURN_CONDITION_LABELS[l.condition]}</span>
                          <b>{item.name}</b>
                          <span className="muted">→ {AREA_LABEL[RETURN_CONDITION_AREAS[l.condition]]}</span>
                        </div>
                        <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                          <button className="icon-btn" aria-label="Bir azalt" onClick={() => change(l.key, { qty: l.qty - 1 })}>
                            −
                          </button>
                          <b style={{ fontSize: 20, minWidth: 28, textAlign: 'center' }}>{l.qty}</b>
                          {isSupervisor && (
                            <button className="icon-btn" aria-label="Bir artır" onClick={() => add(item, l.condition)}>
                              +
                            </button>
                          )}
                        </div>
                      </div>
                      {l.condition !== 'sellable' && (
                        <div className="stack" style={{ gap: 6 }}>
                          <div className="row" style={{ gap: 6 }}>
                            {RETURN_DAMAGE_REASONS.map((r) => (
                              <button key={r} className={`chip select${l.reason === r ? ' on' : ''}`} onClick={() => change(l.key, { reason: r })}>
                                {r}
                              </button>
                            ))}
                          </div>
                          <input
                            className="input"
                            placeholder="Hasar gerekçesi (zorunlu)"
                            value={l.reason}
                            aria-label={`${item.name} hasar gerekçesi`}
                            onChange={(e) => change(l.key, { reason: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="form-grid">
              <div className="field">
                <label htmlFor="ret-code">İade kargo kodu</label>
                <input id="ret-code" className="input mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Kolinin üzerindeki kod" />
              </div>
              <div className="field wide">
                <label htmlFor="ret-note">Not</label>
                <input id="ret-note" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn. kutu ıslak geldi" />
              </div>
            </div>
            {finish.banner}
            <div className="row">
              <button className="btn primary big" disabled={busy || finish.waiting || units === 0 || missingReason} onClick={() => void finish.run('İadeyi tamamla', submit)}>
                <Icon name="check" /> İadeyi tamamla ({units} adet)
              </button>
              {missingReason && <span className="hint bad">Satılamayacak ürünler için hasar gerekçesi girin</span>}
            </div>
          </>
        )}
      </div>
      {asking && (
        <Modal title={`${asking.name}: durumu nedir?`} onClose={() => setAsking(null)}>
          <div className="choice-list">
            {RETURN_CONDITIONS.map((c) => (
              <button
                key={c}
                className={`btn big block${c === 'sellable' ? ' primary' : ''}`}
                onClick={() => {
                  add(asking, c);
                  setAsking(null);
                }}
              >
                {RETURN_CONDITION_LABELS[c]}
                <span className="muted" style={{ fontWeight: 500 }}>
                  {' '}
                  · {AREA_LABEL[RETURN_CONDITION_AREAS[c]]}
                </span>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </section>
  );
}

function ReceivedBanner({ result, canUndo, onUndo, onClose }: { result: ReceiveReturnResult; canUndo: boolean; onUndo: () => void; onClose: () => void }) {
  const r = result.return;
  return (
    <div className="banner ok rdesk-result">
      <Icon name="check" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <b>
          {r.rmaNumber} · {r.orderNumber} teslim alındı.
        </b>{' '}
        {r.sellableUnits > 0 && (
          <>
            {r.sellableUnits} sağlam adet için yerleştirme görevi açıldı (<a href="#/yerlestirme">Yerleştirme</a>).{' '}
          </>
        )}
        {r.blockedUnits > 0 && <>{r.blockedUnits} adet satışa kapalı alana ayrıldı. </>}
        {result.charge && <>İade elleçleme ücreti {fmt.money(result.charge.amount)} satıcının hizmet ücretlerine eklendi. </>}
        {result.orderStatus === 'returned' && <>Siparişin gönderilen ürünlerinin tamamı iade alındı.</>}
        <div className="muted" style={{ marginTop: 4 }}>
          {r.lines.map((l, i) => (
            <div key={i}>
              {l.qty} × {l.sku} {RETURN_CONDITION_LABELS[l.condition].toLocaleLowerCase('tr')} → <span className="nowrap">{l.locationCode}</span>
              {l.reason && ` (${l.reason})`}
            </div>
          ))}
        </div>
        {canUndo && (
          <div className="row rcv-actions" style={{ marginTop: 10 }}>
            <button className="btn" onClick={onUndo}>
              <Icon name="undo" /> Geri al
            </button>
            <span className="muted" style={{ fontWeight: 500 }}>
              Yanlış sipariş ya da durum seçildiyse: {RETURN_UNDO_MINUTES} dakika içinde.
            </span>
          </div>
        )}
      </div>
      <button className="icon-btn" onClick={onClose} aria-label="Kapat">
        <Icon name="x" size={16} />
      </button>
    </div>
  );
}

/** After a return: its sound units go back to the shelf; with none, the floor's next work. */
function ReturnNext({ sellable }: { sellable: number }) {
  return sellable > 0 ? (
    <NextStep
      title="Ürünleri Rafa iade ile yerleştirin"
      steps={[`${sellable} sağlam adet iade alanında bekliyor.`, 'Yerleştirme ekranındaki Rafa iade görevleriyle raflarına koyun.']}
      go={{ label: 'Rafa iadeye geç', to: '/yerlestirme', icon: 'shelf' }}
    />
  ) : (
    <NextWorkStep from="/iade" tone="ok" title="İade tamamlandı" steps={['Satılamayan ürünler ayrı alana kondu; rafa dönecek ürün yok.']} />
  );
}

function UndoneBanner({ result, units, sellable, onClose }: { result: UndoReturnResult; units: number; sellable: number; onClose: () => void }) {
  const r = result.return;
  return (
    <div className="banner warn rdesk-result">
      <Icon name="undo" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <b>
          {r.rmaNumber} · {r.orderNumber} geri alındı.
        </b>{' '}
        {units} adet stoktan düştü{sellable > 0 ? ', yerleştirme görevi iptal edildi' : ''}
        {result.chargeRemoved ? ', iade elleçleme ücreti silindi' : ''}.{' '}
        {r.status === 'expected' ? 'İade yeniden “Beklenen iadeler” listesinde. ' : ''}
        Ürünleri koliye geri koyun; koliyi yeniden okutup doğru şekilde alın.
      </div>
      <button className="icon-btn" onClick={onClose} aria-label="Kapat">
        <Icon name="x" size={16} />
      </button>
    </div>
  );
}

/** Asks before a return is taken back: what happens to its units, its putaway and its fee. */
function UndoReturnDialog(props: { ret: CustomerReturnView; onClose: () => void; onUndone: (result: UndoReturnResult) => void }) {
  const r = props.ret;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    setBusy(true);
    setError(null);
    try {
      props.onUndone(await api<UndoReturnResult>('POST', `/api/returns/${r.id}/undo`));
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`${r.rmaNumber} geri alınsın mı?`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy} onClick={() => void undo()}>
            <Icon name="undo" /> İadeyi geri al
          </button>
        </>
      }
    >
      <div className="stack rcv-dialog">
        {error && <div className="banner bad">{error}</div>}
        <p style={{ margin: 0 }}>
          <b className="mono">{r.orderNumber}</b> · {r.sellableUnits + r.blockedUnits} adet
          {r.blockedUnits > 0 ? ` (${r.sellableUnits} sağlam, ${r.blockedUnits} ayrılan)` : ''}
        </p>
        <ul className="rdesk-effects">
          <li>Ürünler stoktan düşer{r.sellableUnits > 0 ? '; yerleştirme görevi iptal edilir' : ''}.</li>
          <li>İade elleçleme ücreti silinir.</li>
          <li>Sipariş iade alınmadan önceki durumuna döner; satıcı bu iadeyi bildirdiyse iade yeniden beklenir.</li>
        </ul>
        {/* where the desk put each unit: the worker fetches them back into the parcel */}
        <div>
          <b>Ürünleri alıp koliye geri koyun:</b>
          <ul className="rdesk-effects" style={{ marginTop: 4 }}>
            {r.lines.map((l, i) => (
              <li key={i}>
                {l.qty} × {l.sku} → <span className="mono nowrap">{l.locationCode}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          Sonra koliyi yeniden okutup doğru şekilde alın. Rafa yerleştirilmiş ürün varsa iade geri alınamaz.
        </p>
      </div>
    </Modal>
  );
}
